import { Box, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PageTitle from '../../components/PageTitle'
import palette from '../../../theme/palette'
import { customShadows } from '../../../theme/shadows'
import { C, fadeUp, onColor, textOn } from './aboutUi'
import WhatTab from './WhatTab'
import WhyTab from './WhyTab'
import ImpactTab from './ImpactTab'
import SignalsTab from './SignalsTab'
import { fetchPaceSignals } from './signalsService'
import MerchantSelect from '../AllocationConfig/MerchantSelect'

const TABS = [
  { key: 'what', label: 'What', sub: 'What PACE is & how it decides', icon: <LightbulbOutlinedIcon />, color: C.primary },
  { key: 'why', label: 'Why', sub: 'The problem it solves', icon: <HelpOutlineRoundedIcon />, color: C.warning },
  { key: 'impact', label: 'Impact', sub: 'What changes for your business', icon: <TrendingUpIcon />, color: C.success },
  { key: 'signals', label: 'Signals', sub: 'Every parameter PACE reads', icon: <TravelExploreOutlinedIcon />, color: C.violet },
]

const MerchantPicker = (props) => <MerchantSelect {...props} label='Merchant (none = all signals)' />

// Hero illustration: parcels fly from the order into the PACE core, then out to the best courier.
function HeroOrbit() {
  const couriers = [
    { y: 34, c: C.info, best: false },
    { y: 92, c: C.success, best: true },
    { y: 150, c: C.warning, best: false },
    { y: 208, c: C.error, best: false },
  ]
  return (
    <Box component='svg' viewBox='0 0 420 240' sx={{ width: '100%', maxWidth: 460, height: 'auto', display: 'block' }} role='img' aria-label='An order flowing through PACE to the best courier'>
      <defs>
        <radialGradient id='core' cx='50%' cy='50%' r='50%'>
          <stop offset='0%' stopColor='#fff' stopOpacity='.95' />
          <stop offset='100%' stopColor='#fff' stopOpacity='.15' />
        </radialGradient>
      </defs>
      {/* order -> core */}
      <path id='in' d='M52 120 C110 120, 130 120, 190 120' stroke='rgba(255,255,255,.55)' strokeWidth='2' strokeDasharray='5 7' fill='none'>
        <animate attributeName='stroke-dashoffset' from='0' to='-24' dur='1s' repeatCount='indefinite' />
      </path>
      {couriers.map((k, i) => (
        <path key={i} id={`out${i}`} d={`M230 120 C290 120, 300 ${k.y}, 350 ${k.y}`}
          stroke={k.best ? '#fff' : 'rgba(255,255,255,.28)'} strokeWidth={k.best ? 3 : 1.5} strokeDasharray={k.best ? '0' : '4 6'} fill='none' />
      ))}
      {/* order box */}
      <g>
        <rect x='14' y='98' width='44' height='44' rx='10' fill='#fff' />
        <path d='M24 112 l12 -6 l12 6 v16 l-12 6 l-12 -6z M24 112 l12 6 l12 -6 M36 118 v16' stroke={C.primary} strokeWidth='2' fill='none' strokeLinejoin='round' />
      </g>
      {/* core */}
      <circle cx='210' cy='120' r='40' fill='url(#core)'>
        <animate attributeName='r' values='36;42;36' dur='2.4s' repeatCount='indefinite' />
      </circle>
      <circle cx='210' cy='120' r='28' fill='#fff' />
      <text x='210' y='125' textAnchor='middle' fontSize='14' fontWeight='700' fill={C.primary}>PACE</text>
      <circle cx='210' cy='120' r='52' fill='none' stroke='rgba(255,255,255,.35)' strokeDasharray='2 8' strokeWidth='2'>
        <animateTransform attributeName='transform' type='rotate' from='0 210 120' to='360 210 120' dur='14s' repeatCount='indefinite' />
      </circle>
      {/* couriers */}
      {couriers.map((k, i) => (
        <g key={i}>
          <rect x='350' y={k.y - 16} width='56' height='32' rx='9' fill={k.best ? '#fff' : 'rgba(255,255,255,.18)'} />
          <path d={`M360 ${k.y + 6} h22 v-12 h-22z M382 ${k.y - 2} h8 l6 6 v2 h-14z`} fill={k.best ? k.c : 'rgba(255,255,255,.7)'} />
          <circle cx='366' cy={k.y + 8} r='3' fill={k.best ? C.ink : 'rgba(255,255,255,.7)'} />
          <circle cx='390' cy={k.y + 8} r='3' fill={k.best ? C.ink : 'rgba(255,255,255,.7)'} />
          {k.best && (
            <g>
              <circle cx='404' cy={k.y - 14} r='9' fill={C.success} stroke='#fff' strokeWidth='2' />
              <path d={`M399.5 ${k.y - 14} l3 3 l5 -6`} stroke='#fff' strokeWidth='2' fill='none' strokeLinecap='round' />
            </g>
          )}
        </g>
      ))}
      {/* moving parcels */}
      <rect width='10' height='10' rx='2' fill={palette.warning.light} x='-5' y='-5'>
        <animateMotion dur='3s' repeatCount='indefinite' keyPoints='0;1;1' keyTimes='0;.4;1' calcMode='linear'><mpath href='#in' /></animateMotion>
      </rect>
      <rect width='10' height='10' rx='2' fill={palette.warning.light} x='-5' y='-5'>
        <animateMotion dur='3s' repeatCount='indefinite' keyPoints='0;0;1' keyTimes='0;.55;1' calcMode='linear'><mpath href='#out1' /></animateMotion>
      </rect>
    </Box>
  )
}

export default function PaceAboutPage() {
  const [params, setParams] = useSearchParams()
  const active = TABS.find((t) => t.key === params.get('tab'))?.key || 'what'
  const select = (key) => setParams({ tab: key }, { replace: true })

  return (
    <Box sx={{ pb: 8, minHeight: '100%', overflowX: 'clip' }}>
      <PageTitle>PACE · About PACE</PageTitle>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', borderRadius: 2, color: '#fff', px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 },
          background: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.main} 100%)`,
        }}
      >
        <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1.1fr .9fr' }, gap: 4, alignItems: 'center' }}>
          <Box sx={{ animation: `${fadeUp} .5s ease both` }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 99, background: 'rgba(255,255,255,.16)', fontSize: 13, fontWeight: 600 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} /> AI courier allocation by Prozo
            </Box>
            <Typography variant='h2' sx={{ color: 'inherit', fontSize: { xs: 34, md: 44 }, lineHeight: 1.2, mt: 2 }}>
              PACE
            </Typography>
            <Typography variant='h6' sx={{ color: 'inherit', fontSize: { xs: 16, md: 19 }, fontWeight: 600, lineHeight: 1.5, mt: 1.5, opacity: 0.95, maxWidth: 560 }}>
              For every order, pick the courier most likely to deliver it the first time, on time and at the right price.
            </Typography>
            <Typography variant='body2' sx={{ mt: 1.5, opacity: 0.85, maxWidth: 540 }}>
              PACE learns from millions of real shipments. It predicts how each courier will perform on each order and ranks them by your business goals.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
              {['Predicts, not guesses', 'Explains every decision', 'You set the goals', 'Zero-risk rollout'].map((t) => (
                <Box key={t} sx={{ px: 1.5, py: 0.6, borderRadius: 99, border: '1px solid rgba(255,255,255,.4)', fontSize: 13, fontWeight: 500 }}>{t}</Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}><HeroOrbit /></Box>
        </Box>
      </Box>

      {/* ── Tab switcher ─────────────────────────────────────── */}
      <Box
        role='tablist'
        sx={{
          position: 'sticky', top: 0, zIndex: 5, mt: -3.5, mx: { xs: 1, md: 4 }, p: 1, borderRadius: 2, background: '#fff',
          border: `1px solid ${palette.divider}`, boxShadow: customShadows.z8, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1,
        }}
      >
        {TABS.map((t) => {
          const on = t.key === active
          return (
            <Box
              key={t.key}
              role='tab'
              aria-selected={on}
              tabIndex={0}
              onClick={() => select(t.key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select(t.key) }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1, md: 1.5 }, borderRadius: 1.5, cursor: 'pointer', transition: 'background-color .2s ease',
                color: on ? onColor(t.color) : C.ink, background: on ? t.color : 'transparent',
                '&:hover': { background: on ? t.color : `${t.color}14` },
                outline: 'none', '&:focus-visible': { boxShadow: `0 0 0 3px ${t.color}55` },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'grid' }, placeItems: 'center', width: 40, height: 40, borderRadius: 1.5, background: on ? (onColor(t.color) === '#fff' ? 'rgba(255,255,255,.2)' : 'rgba(33,43,54,.1)') : `${t.color}1F`, color: on ? onColor(t.color) : textOn(t.color) }}>
                {t.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, md: 16 }, lineHeight: 1.2 }}>{t.label}</Typography>
                <Typography sx={{ fontSize: 12, opacity: on ? 0.9 : 0.7, display: { xs: 'none', md: 'block' } }}>{t.sub}</Typography>
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box key={active} sx={{ mt: 4, px: { xs: 0, md: 2 }, animation: `${fadeUp} .4s ease both` }}>
        {active === 'what' && <WhatTab onNext={() => select('why')} />}
        {active === 'why' && <WhyTab onNext={() => select('impact')} />}
        {active === 'impact' && <ImpactTab />}
        {active === 'signals' && <SignalsTab load={fetchPaceSignals} MerchantPicker={MerchantPicker} />}
      </Box>
    </Box>
  )
}
